import { Target, X } from "lucide-react";

const money2 = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Loan target-payoff plan card. Shows the saved accelerated-payoff commitment
 * (new rate, new term, extra principal), payoff horizon, and live on-track /
 * ahead / behind progress chip derived from server-side variance calculation.
 */
export default function LoanPlanCard({ plan, progress, onRemove }) {
  const status = progress?.status || "on_track";
  const variance = Number(progress?.variance || 0);
  const chipClass = status === "ahead"
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : status === "behind"
      ? "bg-amber-100 text-amber-800 border-amber-300"
      : "bg-slate-100 text-slate-700 border-slate-300";
  const chipLabel = status === "ahead"
    ? `Ahead by ${money2(Math.abs(variance))}`
    : status === "behind"
      ? `Behind by ${money2(Math.abs(variance))}`
      : "On track";
  const extra = Number(plan.extra_principal || 0);
  return (
    <div className="mt-4 rounded-sm border border-indigo-200 bg-indigo-50/60 p-4" data-testid="loan-plan-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Target className="w-4 h-4 text-indigo-700 mt-0.5" />
          <div>
            <div className="overline text-indigo-900">Target payoff plan</div>
            <div className="mt-0.5 text-sm text-indigo-950 font-mono">
              {plan.new_rate}% · {plan.new_term_years}yr {plan.new_loan_type === "interest_only" ? "IO" : ""}
              {extra > 0 && <> · <b>+{money2(extra)}/mo</b> extra principal</>}
            </div>
            <div className="text-[11px] text-indigo-900/80 mt-0.5 font-mono">
              Payoff in <b>{plan.planned_months}</b> months · base {money2(plan.base_monthly)}/mo
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-[11px] font-mono font-semibold px-2 py-1 border rounded-sm ${chipClass}`}
            data-testid="loan-plan-status-chip"
          >
            {chipLabel}
          </span>
          <button
            onClick={onRemove}
            title="Remove target plan"
            data-testid="loan-plan-remove-button"
            className="text-indigo-700/70 hover:text-red-600 p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {progress && progress.months_elapsed > 0 && (
        <div className="mt-3 pt-3 border-t border-indigo-200/60 grid grid-cols-3 gap-2 text-[11px] font-mono">
          <div>
            <div className="text-indigo-900/60 uppercase tracking-widest">Elapsed</div>
            <div className="text-indigo-950 font-bold">{progress.months_elapsed} mo</div>
          </div>
          <div>
            <div className="text-indigo-900/60 uppercase tracking-widest">Expected paid</div>
            <div className="text-indigo-950 font-bold">{money2(progress.expected_principal_to_date)}</div>
          </div>
          <div>
            <div className="text-indigo-900/60 uppercase tracking-widest">Actual paid</div>
            <div className="text-indigo-950 font-bold">{money2(progress.actual_principal_to_date)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
